import { useState, useEffect, useCallback } from "react";
import { cancelChatService, CancelChatMessage, CancelChatSender } from "../services/membership/cancelChatService";
import { Language } from "../utils/dictionary";
import toast from "react-hot-toast";

interface UseCancelChatReturn {
  messages: CancelChatMessage[];
  sessionId: string | null;
  canCancel: boolean;
  downsell: boolean;
  recovered: boolean;
  isLoading: boolean;
  sendMessage: (text: string, prependMessages?: CancelChatMessage[]) => Promise<void>;
  clearChat: () => void;
  initializeChat: () => void;
  getLastUserMessage: () => string | null;
  addBotMessage: (text: string) => void;
}

export function useCancelChat(language: Language): UseCancelChatReturn {
  const [messages, setMessages] = useState<CancelChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [canCancel, setCanCancel] = useState(false);
  const [downsell, setDownsell] = useState(false);
  const [recovered, setRecovered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadSavedState = useCallback(() => {
    const saved = cancelChatService.loadChatState();
    if (saved) {
      setMessages(saved.messages);
      setSessionId(saved.sessionId);
      setCanCancel(saved.canCancel);
      setDownsell(saved.downsell);
      setRecovered(saved.recovered);
    }
  }, []);

  const saveState = useCallback(
    (newMessages: CancelChatMessage[], newSessionId: string | null, newCanCancel: boolean, newDownsell: boolean, newRecovered: boolean) => {
      cancelChatService.saveChatState({
        messages: newMessages,
        sessionId: newSessionId,
        canCancel: newCanCancel,
        downsell: newDownsell,
        recovered: newRecovered,
      });
    },
    []
  );

  const initializeChat = useCallback(() => {
    loadSavedState();
  }, [loadSavedState]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setCanCancel(false);
    setDownsell(false);
    setRecovered(false);
    cancelChatService.clearChatState();
  }, []);

  const addBotMessage = useCallback((text: string) => {
    const botMessage: CancelChatMessage = {
      id: cancelChatService.generateMessageId(),
      text,
      sender: CancelChatSender.BOT,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, botMessage]);
  }, []);

  const sendMessage = useCallback(
    async (text: string, prependMessages?: CancelChatMessage[]) => {
      if (!text.trim() || isLoading) return;

      setIsLoading(true);

      const userMessage: CancelChatMessage = {
        id: cancelChatService.generateMessageId(),
        text: text.trim(),
        sender: CancelChatSender.USER,
        timestamp: Date.now(),
      };

      const base = prependMessages && messages.length === 0 ? prependMessages : messages;
      const updatedMessages = [...base, userMessage];
      setMessages(updatedMessages);

      try {
        const languageCode = language;

        const response = await cancelChatService.sendMessage(text.trim(), sessionId, languageCode);

        const botMessage: CancelChatMessage = {
          id: cancelChatService.generateMessageId(),
          text: response.output.message,
          sender: CancelChatSender.BOT,
          timestamp: Date.now(),
        };

        const finalMessages = [...updatedMessages, botMessage];

        setMessages(finalMessages);
        setSessionId(response.session_id);
        setCanCancel(response.output.can_cancel);
        setDownsell(response.output.downsell || false);
        setRecovered(response.output.recovered || false);

        saveState(finalMessages, response.session_id, response.output.can_cancel, response.output.downsell || false, response.output.recovered || false);
      } catch (error: any) {
        console.error("Error sending message:", error);

        setMessages(messages);

        toast.error(
          error.response?.data?.message ||
          "Error al enviar el mensaje. Por favor, intenta de nuevo."
        );
      } finally {
        setIsLoading(false);
      }
    },
    [messages, sessionId, isLoading, language, saveState]
  );

  useEffect(() => {
    initializeChat();
  }, [initializeChat]);

  useEffect(() => {
    const checkExpiration = setInterval(() => {
      if (cancelChatService.isChatExpired()) {
        clearChat();
      }
    }, 60000);

    return () => clearInterval(checkExpiration);
  }, [clearChat]);

  const getLastUserMessage = useCallback(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender === CancelChatSender.USER) {
        return messages[i].text;
      }
    }
    return null;
  }, [messages]);

  return {
    messages,
    sessionId,
    canCancel,
    downsell,
    recovered,
    isLoading,
    sendMessage,
    clearChat,
    initializeChat,
    getLastUserMessage,
    addBotMessage,
  };
}
